import { useMemo, useSyncExternalStore } from "react";
import type { Website } from "@shared";
import type { PreviewStatus } from "./types";

type ConsoleError = Website.Errors.ConsoleError;

/**
 * The orthogonal state facts that drive the preview UI, plus a few latched
 * values (stream-entry verdict, iframeKey, projectUuid).
 *
 * Everything the UI needs to decide what to show is derived from this state
 * by `deriveUI`. No other source of truth. See `plans/preview-state-machine.md`.
 */
export interface PreviewState {
  agent: "idle" | "streaming";
  container: PreviewStatus;
  containerError: string | null;
  /**
   * Build verdict from the server-side build. `unknown` is the healthy default;
   * a failed preview deploy flips it to `broken` (see `buildFailed`). The errors
   * carry the sanitized build message the "Fix errors" button hands to the agent.
   */
  build: {
    verdict: "unknown" | "broken";
    errors: ConsoleError[];
  };
  /** Render verdict from the iframe's preview-ready postMessage (or probe timeout). */
  render: {
    phase: "pending" | "rendered" | "timed-out";
    defaultTemplate: boolean;
    since: number;
  };
  /** Captured at stream start. True if the preview was rendered at that moment. */
  streamEnteredHealthy: boolean;
  /** Bumped on every mount. Drives React's iframe key, forcing fresh fetch. */
  iframeKey: number;
  previewUrl: string | null;
  projectUuid: string | null;
}

export type UIMode =
  | {
      mode: "loading";
      step:
        | "idle"
        | "booting"
        | "mounting"
        | "installing"
        | "starting"
        | "agent-working"
        | "rendering";
    }
  | { mode: "iframe"; subtle?: "editing" }
  | {
      mode: "error";
      kind: "container" | "build" | "render" | "generation-failed";
      errors?: ConsoleError[];
      message?: string;
    };

/**
 * The single UI derivation function. Pure — same inputs always produce the
 * same output. Every state transition goes through the store; the component
 * layer only reads this result.
 */
export function deriveUI(s: PreviewState): UIMode {
  if (s.container === "error") {
    return { mode: "error", kind: "container", message: s.containerError ?? "Unknown error" };
  }
  if (s.container !== "ready") {
    return { mode: "loading", step: s.container };
  }

  if (s.agent === "streaming") {
    return s.streamEnteredHealthy
      ? { mode: "iframe", subtle: "editing" }
      : { mode: "loading", step: "agent-working" };
  }

  // agent idle. A broken build is ground truth, surfaced BEFORE the mount /
  // render gates: a failed build never produces a previewUrl to mount, so
  // iframeKey stays 0 — gating the error behind `iframeKey > 0` would bury it
  // under a perpetual loading spinner (the bug where a broken </div> only
  // showed the real error after a manual page reload).
  if (s.build.verdict === "broken") {
    return { mode: "error", kind: "build", errors: s.build.errors };
  }

  // Before the first mount the iframe would serve the snapshot placeholder;
  // keep the loading overlay up until a real user mount has happened.
  if (s.iframeKey === 0) {
    return { mode: "loading", step: "rendering" };
  }
  if (s.render.phase === "pending") {
    return { mode: "loading", step: "rendering" };
  }
  if (s.render.phase === "timed-out") {
    return { mode: "error", kind: "render" };
  }
  if (s.render.defaultTemplate) {
    return { mode: "error", kind: "generation-failed" };
  }
  return { mode: "iframe" };
}

export const PROBE_TIMEOUT_MS = 20_000;

const initialState: PreviewState = {
  agent: "idle",
  container: "idle",
  containerError: null,
  build: { verdict: "unknown", errors: [] },
  render: { phase: "pending", defaultTemplate: false, since: 0 },
  streamEnteredHealthy: false,
  iframeKey: 0,
  previewUrl: null,
  projectUuid: null,
};

type Listener = () => void;

class PreviewStateStoreClass {
  private state: PreviewState = initialState;
  private listeners: Set<Listener> = new Set();
  private probeTimer: ReturnType<typeof setTimeout> | null = null;

  // ------------------------------------------------------------------
  // Read API
  // ------------------------------------------------------------------

  getState(): PreviewState {
    return this.state;
  }

  getUI(): UIMode {
    return deriveUI(this.state);
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // ------------------------------------------------------------------
  // Transitions (only way to mutate state)
  // ------------------------------------------------------------------

  /** LangGraph stream transitioned to streaming=true. */
  agentStreamStarted(): void {
    this.set({
      agent: "streaming",
      streamEnteredHealthy: this.state.render.phase === "rendered" && this.state.build.verdict !== "broken",
    });
  }

  /** LangGraph stream transitioned to streaming=false. */
  agentStreamEnded(): void {
    this.set({ agent: "idle" });
  }

  /**
   * Phase 1 of a mount cycle: mask the UI before the new preview is ready.
   * Sets render.pending so the iframe is hidden behind the loading overlay and
   * clears the stale build verdict. Does NOT bump iframeKey (see `iframeReload`)
   * and does NOT arm the probe timer: the server build that follows is unbounded
   * (a cold `astro build` can take many seconds), and the probe is a *render*
   * deadline, not a *build* deadline — arming it here flashed RenderTimeoutCard
   * mid-build on a slow rebuild. The probe is armed in `iframeReload`, once a
   * real iframe is mounted at the new URL and we're genuinely waiting on its
   * `load` event. Pair with `iframeReload()` after the build completes.
   */
  mountStarted(): void {
    this.clearProbeTimer();
    this.set({
      render: { phase: "pending", defaultTemplate: false, since: Date.now() },
      build: { verdict: "unknown", errors: [] },
    });
  }

  /**
   * Phase 2 of a mount cycle: bump iframeKey so React remounts the iframe with
   * a fresh GET. Call this AFTER the new preview URL is available so the first
   * request reflects the new build. Arms the probe timer here — this is the
   * point a concrete iframe starts fetching, so the timeout now bounds the
   * iframe render (its `load` event clears it), not the preceding build.
   */
  iframeReload(): void {
    this.clearProbeTimer();
    this.set({
      iframeKey: this.state.iframeKey + 1,
      render: { phase: "pending", defaultTemplate: false, since: Date.now() },
    });
    this.startProbeTimer();
  }

  /** Forwards container status + previewUrl + error into the store. */
  containerStateChanged(next: {
    status: PreviewStatus;
    previewUrl: string | null;
    error?: string | null;
  }): void {
    this.set({
      container: next.status,
      previewUrl: next.previewUrl,
      containerError: next.error ?? null,
    });
  }

  /**
   * The server-side build failed. Surface it as ground truth: mark the build
   * broken with the (already-sanitized) error message so `deriveUI` renders the
   * BuildErrorCard and the "Fix errors" button can hand the error to the agent.
   *
   * Container is set `ready` — a failed build is a *content* error (the agent
   * wrote something that won't compile), not an infra/container failure, so it
   * must NOT route to the generic ContainerErrorCard + page-reload. The probe
   * timer is cleared since there's nothing left to render.
   */
  buildFailed(message: string | null): void {
    this.clearProbeTimer();
    const error: ConsoleError = {
      type: "error",
      message: message?.trim() || "Preview build failed",
      timestamp: new Date(),
    };
    this.set({
      container: "ready",
      containerError: null,
      build: { verdict: "broken", errors: [error] },
    });
  }

  /** Called from the `preview-ready` postMessage handler in WebsitePreview. */
  iframeRendered({ defaultTemplate }: { defaultTemplate: boolean }): void {
    this.clearProbeTimer();
    this.set({
      render: { phase: "rendered", defaultTemplate, since: Date.now() },
      // Ground truth: if the iframe rendered, the build is healthy.
      build: { verdict: "unknown", errors: [] },
    });
  }

  /**
   * Fresh project selected. Resets everything (and tears down the probe timer).
   * Call on route/project change.
   */
  projectSwitched(uuid: string | null): void {
    this.clearProbeTimer();
    this.state = { ...initialState, projectUuid: uuid };
    this.emit();
  }

  /** Errors that feed the "Fix errors" button payload. */
  collectFixableErrors(): ConsoleError[] {
    return this.state.build.errors.filter((e) => e.type === "error");
  }

  // ------------------------------------------------------------------
  // Internal helpers
  // ------------------------------------------------------------------

  private set(partial: Partial<PreviewState>): void {
    this.state = { ...this.state, ...partial };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (e) {
        console.error("[PreviewStateStore] listener threw", e);
      }
    }
  }

  private startProbeTimer(): void {
    this.probeTimer = setTimeout(() => {
      this.probeTimer = null;
      if (this.state.render.phase === "pending") {
        this.set({
          render: { ...this.state.render, phase: "timed-out" },
        });
      }
    }, PROBE_TIMEOUT_MS);
  }

  private clearProbeTimer(): void {
    if (this.probeTimer) {
      clearTimeout(this.probeTimer);
      this.probeTimer = null;
    }
  }

  // ------------------------------------------------------------------
  // Test-only helpers
  // ------------------------------------------------------------------

  /** Reset to the initial state. Only used by tests. */
  __resetForTests(): void {
    this.clearProbeTimer();
    this.state = initialState;
    this.listeners.clear();
  }
}

// Singleton — preserves across HMR.
let instance: PreviewStateStoreClass;
if (typeof import.meta !== "undefined" && import.meta.hot?.data) {
  instance = import.meta.hot.data.previewStateStore ?? new PreviewStateStoreClass();
  import.meta.hot.data.previewStateStore = instance;
} else {
  instance = new PreviewStateStoreClass();
}

export const PreviewStateStore = instance;

// Expose for e2e testing + debugging.
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__PreviewStateStore = instance;
}

// ----------------------------------------------------------------------
// React hooks
// ----------------------------------------------------------------------

export function usePreviewState(): PreviewState {
  return useSyncExternalStore(
    PreviewStateStore.subscribe,
    () => PreviewStateStore.getState(),
    () => PreviewStateStore.getState(),
  );
}

export function usePreviewUI(): UIMode {
  const state = usePreviewState();
  return deriveUI(state);
}

/** Build errors (errors only) — feeds the "Fix errors" button. */
export function useConsoleErrors(): ConsoleError[] {
  const state = usePreviewState();
  return useMemo(
    () => state.build.errors.filter((e) => e.type === "error"),
    [state.build.errors],
  );
}
